/**
 * @file ConsentTextsGroup.tsx
 * @description Группа «Тексты» панели «Счётчики и cookie» (phase19.16,
 * партия Р3г, v0.19.62; SB134 «владелец правит только вступление (до 300
 * знаков, простой текст), фразу про режим, список сервисов и кнопки ставит
 * платформа»; решение пользователя 2026-10-23 — тексты платформы редакциями,
 * переход на новую редакцию — «Обновить» владельцем).
 *
 * Вступление живёт в поле: в часть уходит только прошедшее проверку
 * (`consentIntroIssue`), без пробелов по краям; пусто — вступление
 * платформы, «↺» возвращает его. Ниже — «Редакция N от <дата>» и итоговый
 * текст, как его увидит посетитель, по текстам этой редакции
 * (`consentPlatform.banner.texts`, пока сервер их не отдаёт — встроенные).
 * Платформа выпустила редакцию новее черновика — полоса «[Обновить]»:
 * запись части с новым `platformTextVersion`, на сайт — обычной публикацией.
 */

import { SITE_LIMITS } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";
import { useId, useState } from "react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { Textarea } from "../../ui/textarea";
import { useCounters } from "./counters-context";
import { useCurrentContext } from "../../../auth/hooks";
import { bannerEditionLabel, bannerPreviewText, bannerTextsFor, bannerTextsUpdate } from "../../../lib/site-view/consent-platform";
import { INTRO_PLATFORM_TEXT, introError, textsSummary } from "../../../lib/site-view/counters-form";

const MAX = SITE_LIMITS.consentIntro;

export function ConsentTextsGroup() {
  const { counters, platform, noticeAllowed, update } = useCounters();
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const id = useId();
  const value = counters.intro;
  const [text, setText] = useState(value);
  // Значение сменилось снаружи — показать его, если своё не отличается лишь пробелами по краям.
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    if (text.trim() !== value) setText(value);
  }
  const error = introError(text);
  const length = [...text.trim()].length;
  const texts = bannerTextsFor(counters, platform);
  const preview = bannerPreviewText(counters, noticeAllowed, texts);
  const nextVersion = bannerTextsUpdate(counters, platform);

  function onInput(raw: string) {
    setText(raw);
    const next = raw.trim();
    if (introError(raw) === null && next !== value) update({ intro: next });
  }

  return (
    <SettingsGroup
      id="texts"
      title="Тексты"
      hint="Вступление баннера — остальное ставит платформа."
      summary={textsSummary(counters)}
      status={nextVersion !== null ? "warning" : undefined}
    >
      {nextVersion !== null ? (
        <NoticeStrip tone="warning" action={<Button variant="outline" onClick={() => update({ platformTextVersion: nextVersion })}>Обновить</Button>}>
          Платформа выпустила редакцию {nextVersion} текстов баннера. После «Обновить» новые тексты попадут на сайт с публикацией вида сайта.
        </NoticeStrip>
      ) : null}
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <Label htmlFor={id}>Вступление</Label>
          {value !== "" ? (
            <Button variant="ghost" size="sm" className="min-h-12 can-hover:min-h-8" onClick={() => onInput("")} aria-label="Вступление: как у платформы">
              <RotateCcw aria-hidden />
              как у платформы
            </Button>
          ) : null}
        </div>
        <Textarea
          id={id}
          rows={3}
          value={text}
          placeholder={texts.introDefault}
          aria-invalid={error !== null}
          aria-describedby={`${id}-hint`}
          onChange={(e) => onInput(e.target.value)}
        />
        <div id={`${id}-hint`} className="flex flex-wrap justify-between gap-x-3">
          <span className={error ? "type-caption text-destructive" : "type-caption text-on-surface-variant"}>
            {error ? `${error} Пока текст не исправлен, в черновике остаётся прежний.` : "Простой текст. Пусто — вступление платформы."}
          </span>
          <span className="type-caption tabular-nums text-on-surface-variant">
            {length}/{MAX}
          </span>
        </div>
      </div>
      <div className="flex min-w-0 flex-col gap-1 rounded-xl bg-surface-low p-3">
        <span className="type-caption text-on-surface-variant">Так увидит посетитель · {bannerEditionLabel(counters, platform)} текстов платформы</span>
        <p className="type-body break-words text-on-surface">{preview ?? "Баннера не будет: счётчики не подключены и уведомление без них выключено."}</p>
      </div>
      <FieldHint className="mt-0">{INTRO_PLATFORM_TEXT}</FieldHint>
      <FieldHint className="mt-0">«Подробнее» ведёт на политику обработки персональных данных, если она опубликована.</FieldHint>
      <Link to={`/shop/${slug}/settings/legal-documents`} className="type-body inline-flex min-h-12 items-center self-start font-medium text-primary hover:underline">
        Правовые документы →
      </Link>
    </SettingsGroup>
  );
}
